import React, { useState, useEffect, useCallback } from 'react';
import { Settings, CheckSquare, Globe, FileText, Plus, AlertCircle, X, Send, RefreshCw, ChevronDown, ChevronUp, BookOpen, Copy, Check } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

interface StepChatModalProps {
  open: boolean;
  onClose: () => void;
  step: string;
  projectId: number | undefined;
  stepIndex?: number;
  onStepsExpanded?: (expandedSteps: string[]) => void;
  onStepsUpdated?: () => void; // NEW: Add this prop for real-time updates
}

interface AIOptions {
  useWebSearch: boolean;
  enableStepExpansion: boolean;
  provideCitationOnly: boolean;
  createChecklist: boolean;
}

// Component to format AI response text with proper sections
const FormattedMessage: React.FC<{ text: string }> = ({ text }) => {
  // Parse the text for special formatting patterns
  const formatText = (content: string) => {
    // Split by common section headers
    const sections = content.split(/(\*\*[^:]+:\*\*)/g);
    
    return sections.map((section, index) => {
      // Check if this is a bold header
      if (section.match(/^\*\*[^:]+:\*\*$/)) {
        const headerText = section.replace(/\*\*/g, '').replace(':', '');
        return (
          <div key={index} className="mt-4 mb-2">
            <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              {headerText.includes('Prerequisites') && <AlertCircle className="w-4 h-4 text-orange-500" />}
              {headerText.includes('Stakeholders') && <Settings className="w-4 h-4 text-blue-500" />}
              {headerText.includes('Control') && <CheckSquare className="w-4 h-4 text-green-500" />}
              {headerText.includes('Outcome') && <FileText className="w-4 h-4 text-purple-500" />}
              {headerText}
            </h5>
          </div>
        );
      }
      
      // Format numbered lists
      if (section.includes('\n')) {
        const lines = section.split('\n').filter(line => line.trim());
        const isNumberedList = lines.some(line => /^\d+\./.test(line.trim()));
        const isBulletList = lines.some(line => /^[-•]/.test(line.trim()));
        
        if (isNumberedList || isBulletList) {
          return (
            <ul key={index} className="space-y-2 my-2">
              {lines.map((line, lineIndex) => {
                const cleanLine = line.replace(/^[\d]+\.\s*/, '').replace(/^[-•]\s*/, '').trim();
                if (!cleanLine) return null;
                
                return (
                  <li key={lineIndex} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-5 h-5 mt-0.5">
                      {isNumberedList ? (
                        <span className="flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          {lineIndex + 1}
                        </span>
                      ) : (
                        <span className="block w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5"></span>
                      )}
                    </span>
                    <span className="text-sm text-gray-700 flex-1">{cleanLine}</span>
                  </li>
                );
              })}
            </ul>
          );
        }
      }
      
      // Regular paragraph
      if (section.trim()) {
        return (
          <p key={index} className="text-sm text-gray-700 leading-relaxed my-2">
            {section.trim()}
          </p>
        );
      }
      
      return null;
    });
  };
  
  // Handle user messages differently (simpler formatting)
  if (!text.includes('**')) {
    return <div className="text-sm whitespace-pre-wrap">{text}</div>;
  }
  
  return <div className="space-y-1">{formatText(text)}</div>;
};

const EnhancedStepChatModal: React.FC<StepChatModalProps> = ({ 
  open, 
  onClose, 
  step, 
  projectId, 
  stepIndex,
  onStepsExpanded,
  onStepsUpdated // NEW: Add this parameter
}) => {
  const [messages, setMessages] = useState<{ 
    role: 'user' | 'ai'; 
    text: string; 
    sources?: any[];
    expandedSteps?: string[];
    checklist?: string[];
  }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [stepsHaveBeenUpdated, setStepsHaveBeenUpdated] = useState(false); // NEW: Track if steps were updated
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [aiOptions, setAIOptions] = useState<AIOptions>({
    useWebSearch: false,
    enableStepExpansion: false,
    provideCitationOnly: false,
    createChecklist: false
  });

  // Helper function to toggle section expansion
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      toast.success('Copied to clipboard!', { duration: 2000 });
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Helper function to format AI response text
  const formatAIResponse = (responseText: string): string => {
    // Remove code block markers and curly braces
    let text = responseText.replace(/```json|```/g, '').trim();
    // If it looks like JSON, try to extract the 'answer' field, else just return the text
    try {
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        // If 'answer' is present and is a string, return it
        if (parsed.answer && typeof parsed.answer === 'string') {
          return parsed.answer;
        }
        // Otherwise, flatten all string values in the object
        return Object.values(parsed).filter(v => typeof v === 'string').join('\n');
      }
    } catch (e) {
      // Ignore JSON parse errors
    }
    // Remove leading/trailing curly braces if present
    if (text.startsWith('{') && text.endsWith('}')) {
      text = text.slice(1, -1).trim();
    }
    // Remove any remaining JSON-like lines
    text = text.replace(/"[^"]+":/g, '').replace(/[{}[\]"]/g, '').replace(/\s+/g, ' ').trim();
    return text;
  };

  const askAI = async (question: string, options: AIOptions) => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    
    try {
      const userMessage = { role: 'user' as const, text: question };
      setMessages(msgs => [...msgs, userMessage]);
      
      // Save user message to database
      await saveChatMessage(userMessage);
      
      const requestBody = {
        projectId,
        step,
        question,
        options,
        stepIndex
      };
    
      // KEEP YOUR ORIGINAL API ENDPOINT - DON'T CHANGE IT!
      const res = await api.post('/api/analysis/ask-ai-step', requestBody);
      
      // Format the AI response text to handle JSON responses properly
      const formattedText = formatAIResponse(res.data.answer);
      
      const aiMessage = {
        role: 'ai' as const,
        text: formattedText,
        sources: res.data.sources,
        expandedSteps: res.data.expanded_steps,
        checklist: res.data.checklist
      };
      
      setMessages(msgs => [...msgs, aiMessage]);
      
      // Save AI message to database
      await saveChatMessage(aiMessage);
      
      // If steps were expanded and callback is provided, notify parent
      if (res.data.expanded_steps && res.data.expanded_steps.length > 0 && onStepsExpanded) {
        onStepsExpanded(res.data.expanded_steps);
        setStepsHaveBeenUpdated(true); // NEW: Mark that steps were updated
        
        // NEW: Show success notification instead of closing modal
        toast.success(`Step expanded with ${res.data.expanded_steps.length} sub-steps! Changes will be visible when you close this chat.`, {
          duration: 4000,
          position: 'top-center'
        });
      }
      
      // Show success message if steps were updated without closing the modal
      if (res.data.steps_updated) {
        setMessages(msgs => [...msgs, {
          role: 'ai' as const,
          text: `✅ Process steps have been updated with ${res.data.expanded_steps?.length || 0} additional sub-steps. These changes will be reflected in Level 3 and Level 4 process maps.`,
          sources: []
        }]);
        
        setStepsHaveBeenUpdated(true); // NEW: Mark that steps were updated
        
        // NEW: Trigger background refresh if callback is provided
        if (onStepsUpdated) {
          setTimeout(() => {
            onStepsUpdated();
          }, 1000);
        }
      }
      
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to get AI response');
      console.error('AI request error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      askAI(input.trim(), aiOptions);
      setInput('');
    }
  };

  const handleQuickAction = (action: string) => {
    let question = '';
    let customOptions = { ...aiOptions };
  
    switch (action) {
      case 'expand':
        question = 'Please provide more detailed sub-steps for this process step, including prerequisites, key stakeholders, control points, and expected outcomes.';
        customOptions.enableStepExpansion = true;
        setAIOptions(prev => ({ ...prev, enableStepExpansion: true }));
        break;
      case 'sources':
        question = 'What are the sources and references for this process step? Please provide exact citations.';
        customOptions.provideCitationOnly = true;
        break;
      case 'checklist':
        question = 'Create a verification checklist for this process step to ensure nothing is missed.';
        customOptions.createChecklist = true;
        break;
      case 'risks':
        question = 'What are the potential risks and control points for this process step?';
        break;
      default:
        return;
    }
    
    askAI(question, customOptions);
  };

  // NEW: Handle close with refresh
  const handleClose = () => {
    if (stepsHaveBeenUpdated && onStepsUpdated) {
      // Refresh the parent component's data before closing
      onStepsUpdated();
    }
    onClose();
    // REMOVED: No more automatic page reload
  };

  // Load chat history from database
  const loadChatHistory = useCallback(async () => {
    if (!projectId || stepIndex === null) return;
    
    try {
      const response = await api.get(`/api/analysis/step-chat-history/${projectId}/${stepIndex}`);
      if (response.data && response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.log('No existing chat history found for this step');
      // Don't show error to user - it's normal to not have chat history
    }
  }, [projectId, stepIndex]);

  // Load existing chat history when modal opens
  useEffect(() => {
    if (open && projectId && stepIndex !== null) {
      loadChatHistory();
    }
    if (!open) {
      setInput('');
      setError('');
      setShowOptions(false);
      setStepsHaveBeenUpdated(false);
      // DON'T clear messages - keep chat history
    }
  }, [open, projectId, stepIndex, loadChatHistory]);

  // Save chat message to database
  const saveChatMessage = async (message: any) => {
    if (!projectId || stepIndex === null) {
      console.log('Skipping chat save - missing projectId or stepIndex:', { projectId, stepIndex });
      return;
    }
    
    console.log('Saving chat message:', {
      projectId,
      stepIndex,
      messageRole: message.role,
      messageLength: message.text?.length || 0,
      step: step?.substring(0, 50) + '...'
    });
    
    try {
      const response = await api.post(`/api/analysis/save-step-chat`, {
        projectId,
        stepIndex,
        step,
        message
      });
      console.log('Chat message saved successfully:', response.data);
    } catch (error: any) {
      console.error('Failed to save chat message:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to save chat message to database');
    }
  };

  // Apply all changes from the chat conversation to the actual process flow
  const applyChangesToProcessFlow = async () => {
    if (!projectId || stepIndex === null || messages.length === 0) {
      toast.error('No conversation to apply');
      return;
    }

    setIsApplyingChanges(true);

    try {
      console.log('Applying changes from conversation to process flow...', {
        projectId,
        stepIndex,
        originalStep: step.substring(0, 50) + '...',
        conversationLength: messages.length
      });
      
      // Send the entire conversation to the backend to analyze and apply changes
      const response = await api.post('/api/analysis/apply-chat-changes', {
        projectId,
        stepIndex,
        originalStep: step,
        conversation: messages,
        requestType: 'apply_conversation_insights'
      });

      console.log('Apply changes response:', response.data);

      if (response.data.success) {
        const changesText = response.data.changesApplied || 'Process flow updated with conversation insights';
        const addedSteps = response.data.addedSteps || 0;
        const modifiedSteps = response.data.modifiedSteps || 0;
        
        let successMessage = changesText;
        if (addedSteps > 0 || modifiedSteps > 0) {
          successMessage += ` (${addedSteps} steps added, ${modifiedSteps} steps modified)`;
        }
        
        toast.success(successMessage, { duration: 5000 });
        setStepsHaveBeenUpdated(true);
        
        // Add a confirmation message to the chat
        setMessages(msgs => [...msgs, {
          role: 'ai' as const,
          text: `✅ Changes applied successfully! ${successMessage}`,
          sources: []
        }]);
        
        // Trigger refresh of parent component with a slight delay
        if (onStepsUpdated) {
          setTimeout(() => {
            onStepsUpdated();
          }, 1000);
        }
      } else {
        console.error('Backend returned success=false:', response.data);
        toast.error('Failed to apply changes to process flow');
      }
    } catch (error: any) {
      console.error('Failed to apply changes:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error applying changes: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col relative">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">
                Ask Jarvis about this step
                {/* NEW: Show update indicator */}
                {stepsHaveBeenUpdated && (
                  <span className="ml-2 text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                    Updated
                  </span>
                )}
              </h3>
              <div className="p-2 bg-light-purple rounded text-gray-800 text-sm max-h-20 overflow-y-auto">
                {step}
              </div>
            </div>
            {/* UPDATED: Use new close handler */}
            <button 
              onClick={handleClose}
              className="ml-4 text-gray-400 hover:text-gray-700 text-xl"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* AI Options Toggle */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleQuickAction('expand')}
                className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md text-sm flex items-center gap-1"
                disabled={loading}
              >
                <Plus className="w-3 h-3" />
                Expand Step
              </button>
              <button
                onClick={() => handleQuickAction('sources')}
                className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md text-sm flex items-center gap-1"
                disabled={loading}
              >
                <FileText className="w-3 h-3" />
                Citations
              </button>
              <button
                onClick={() => handleQuickAction('checklist')}
                className="px-3 py-1 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md text-sm flex items-center gap-1"
                disabled={loading}
              >
                <CheckSquare className="w-3 h-3" />
                Checklist
              </button>
              <button
                onClick={() => handleQuickAction('risks')}
                className="px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md text-sm flex items-center gap-1"
                disabled={loading}
              >
                <AlertCircle className="w-3 h-3" />
                Risks
              </button>
              
              {/* Apply Changes Button - Only show if there are messages */}
              {messages.length > 0 && (
                <button
                  onClick={applyChangesToProcessFlow}
                  disabled={isApplyingChanges || loading}
                  className="px-3 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md text-sm flex items-center gap-1 disabled:opacity-50"
                  title="Apply all insights from this conversation to update the actual process flow"
                >
                  <RefreshCw className={`w-3 h-3 ${isApplyingChanges ? 'animate-spin' : ''}`} />
                  {isApplyingChanges ? 'Applying...' : 'Apply Changes to Process'}
                </button>
              )}
            </div>
            
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="p-2 hover:bg-gray-100 rounded-md"
              title="AI Options"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
          
          {/* AI Options Panel */}
          {showOptions && (
            <div className="mt-3 p-3 bg-gray-50 rounded-md border">
              <h4 className="text-sm font-medium mb-2">AI Options</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiOptions.useWebSearch}
                    onChange={(e) => setAIOptions(prev => ({ ...prev, useWebSearch: e.target.checked }))}
                    className="rounded"
                  />
                  <Globe className="w-4 h-4" />
                  <span className="text-sm">Enable web search</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiOptions.enableStepExpansion}
                    onChange={(e) => setAIOptions(prev => ({ ...prev, enableStepExpansion: e.target.checked }))}
                    className="rounded"
                  />
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Enable step expansion</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiOptions.provideCitationOnly}
                    onChange={(e) => setAIOptions(prev => ({ ...prev, provideCitationOnly: e.target.checked }))}
                    className="rounded"
                  />
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">Citations only</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={aiOptions.createChecklist}
                    onChange={(e) => setAIOptions(prev => ({ ...prev, createChecklist: e.target.checked }))}
                    className="rounded"
                  />
                  <CheckSquare className="w-4 h-4" />
                  <span className="text-sm">Generate checklist</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {error}
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                msg.role === 'user' 
                  ? 'bg-electric-blue text-white' 
                  : 'bg-white border border-gray-200 text-gray-800'
              }`}>
                <FormattedMessage text={msg.text} />
                
                {/* Enhanced Citations Section with Collapsible */}
                {msg.role === 'ai' && Array.isArray(msg.sources) && msg.sources.length > 0 && (
                  <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div 
                      className="flex items-center gap-2 mb-3 cursor-pointer select-none"
                      onClick={() => toggleSection(`citations-${idx}`)}
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <h4 className="text-sm font-semibold text-blue-900">Citations & References</h4>
                      <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {msg.sources.length} source{msg.sources.length > 1 ? 's' : ''}
                      </span>
                      {expandedSections[`citations-${idx}`] ? 
                        <ChevronUp className="w-4 h-4 text-blue-600" /> : 
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      }
                    </div>
                    {(expandedSections[`citations-${idx}`] !== false) && (
                      <div className="space-y-3 animate-in slide-in-from-top-2">
                        {msg.sources.map((src: any, i: number) => {
                          const sourceText = `${src.document}${src.page ? ` (Page ${src.page})` : ''}${src.excerpt ? `\n"${src.excerpt}"` : ''}`;
                          const sourceIndex = idx * 1000 + i; // Unique index for copy functionality
                          
                          return (
                            <div key={i} className="bg-white rounded-md p-3 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-200">
                              <div className="flex items-start gap-2">
                                <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                                  {i + 1}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <BookOpen className="w-3 h-3 text-blue-500" />
                                        <span className="font-medium text-gray-900 text-sm">{src.document}</span>
                                        {src.page && (
                                          <span className="text-xs bg-gradient-to-r from-gray-100 to-gray-50 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                                            Page {src.page}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => copyToClipboard(sourceText, sourceIndex)}
                                      className="p-1 hover:bg-blue-100 rounded transition-colors"
                                      title="Copy citation"
                                    >
                                      {copiedIndex === sourceIndex ? 
                                        <Check className="w-3 h-3 text-green-600" /> : 
                                        <Copy className="w-3 h-3 text-gray-400 hover:text-blue-600" />
                                      }
                                    </button>
                                  </div>
                                  {src.excerpt && (
                                    <blockquote className="mt-2 pl-3 border-l-3 border-blue-300 text-gray-700 text-xs italic bg-blue-50 py-1 pr-2 rounded-r">
                                      "{src.excerpt.length > 200 ? `${src.excerpt.substring(0, 200)}...` : src.excerpt}"
                                    </blockquote>
                                  )}
                                  {src.relevance && (
                                    <div className="mt-2 flex items-center gap-2">
                                      <span className="text-xs text-gray-500">Relevance:</span>
                                      <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, idx) => (
                                          <div
                                            key={idx}
                                            className={`w-2 h-2 rounded-full transition-colors ${
                                              idx < Math.round(src.relevance * 5)
                                                ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                                                : 'bg-gray-200'
                                            }`}
                                          />
                                        ))}
                                      </div>
                                      <span className="text-xs text-gray-400">
                                        ({Math.round(src.relevance * 100)}%)
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Enhanced Expanded Steps Section */}
                {msg.role === 'ai' && Array.isArray(msg.expandedSteps) && msg.expandedSteps.length > 0 && (
                  <div className="mt-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Plus className="w-4 h-4 text-purple-600" />
                      <h4 className="text-sm font-semibold text-purple-900">Expanded Sub-steps</h4>
                      <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        {msg.expandedSteps.length} step{msg.expandedSteps.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {msg.expandedSteps.map((substep: string, i: number) => (
                        <div key={i} className="flex items-start gap-3 bg-white rounded-md p-3 shadow-sm border border-purple-100 hover:shadow-md transition-shadow">
                          <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {i + 1}
                          </div>
                          <p className="text-sm text-gray-700 flex-1">{substep}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Enhanced Checklist Section */}
                {msg.role === 'ai' && Array.isArray(msg.checklist) && msg.checklist.length > 0 && (
                  <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckSquare className="w-4 h-4 text-green-600" />
                      <h4 className="text-sm font-semibold text-green-900">Verification Checklist</h4>
                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        {msg.checklist.length} item{msg.checklist.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {msg.checklist.map((item: string, i: number) => (
                        <div key={i} className="flex items-start gap-3 bg-white rounded-md p-3 shadow-sm border border-green-100 hover:shadow-md transition-shadow group">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-5 h-5 border-2 border-green-500 rounded flex items-center justify-center group-hover:bg-green-50 transition-colors">
                              <CheckSquare className="w-3 h-3 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 flex-1">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-electric-blue"></div>
                <span className="text-sm text-gray-600">Jarvis is thinking...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input Form */}
        <div className="p-4 border-t border-gray-200">
          <form onSubmit={handleSend} className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about this step..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-electric-blue focus:border-electric-blue"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-4 py-2 bg-electric-blue text-white rounded-md hover:bg-electric-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnhancedStepChatModal;